<script setup>
/* eslint-disable vue/custom-event-name-casing */
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InlineEditField from 'dashboard/components-next/pipeline/inline/InlineEditField.vue';

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  customAttributes: {
    type: Array,
    default: () => [],
  },
  contactCustomAttributes: {
    type: Array,
    default: () => [],
  },
  notesCount: {
    type: Number,
    default: 0,
  },
  checklistTotal: {
    type: Number,
    default: 0,
  },
  checklistCompleted: {
    type: Number,
    default: 0,
  },
  attachmentsCount: {
    type: Number,
    default: 0,
  },
  stageChangesCount: {
    type: Number,
    default: 0,
  },
});

// ── Emits ─────────────────────────────────────────────────────────────────────

const emit = defineEmits([
  'goToTab',
  'updateField',
  'unlinkConversation',
  'linkConversation',
  'overrideLeadScore',
]);

// ── Composables ───────────────────────────────────────────────────────────────

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// ── localStorage collapse state ───────────────────────────────────────────────

const STORAGE_KEY_PREFIX = 'pipelineCard.panels.';

const currentUserId = computed(
  () => store.getters.getCurrentUser?.id || 'default'
);

const storageKey = computed(
  () => `${STORAGE_KEY_PREFIX}${currentUserId.value}`
);

const loadPanelState = () => {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore parse errors
  }
  // defaults: all open except additionalData and customFields
  return {
    contact: true,
    statistics: true,
    itemInfo: true,
    linkedConversation: true,
    additionalData: false,
    customFields: false,
  };
};

const panelOpen = ref(loadPanelState());

const savePanelState = () => {
  try {
    localStorage.setItem(storageKey.value, JSON.stringify(panelOpen.value));
  } catch {
    // ignore storage errors
  }
};

watch(panelOpen, savePanelState, { deep: true });

// Re-load when user changes (e.g. account switch)
watch(storageKey, () => {
  panelOpen.value = loadPanelState();
});

const togglePanel = key => {
  panelOpen.value = { ...panelOpen.value, [key]: !panelOpen.value[key] };
};

// ── Contact info ──────────────────────────────────────────────────────────────

const contactAvatarUrl = computed(() => {
  const d = props.item?.item_details;
  if (d?.contact_thumbnail?.trim()) return d.contact_thumbnail;
  const conv = d?.conversation?.contact;
  if (conv?.thumbnail?.trim()) return conv.thumbnail;
  if (conv?.avatar_url?.trim()) return conv.avatar_url;
  if (props.item?.contact?.thumbnail?.trim())
    return props.item.contact.thumbnail;
  return '';
});

const contactName = computed(
  () => props.item?.contact?.name || props.item?.item_details?.company || ''
);

const contactPhone = computed(
  () =>
    props.item?.contact?.phone_number || props.item?.item_details?.phone || ''
);

const contactEmail = computed(
  () => props.item?.contact?.email || props.item?.item_details?.email || ''
);

const contactId = computed(
  () =>
    props.item?.contact?.id ||
    props.item?.item_details?.conversation?.contact?.id ||
    null
);

const navigateToContact = () => {
  if (!contactId.value) return;
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'contacts_show',
      params: { accountId, id: contactId.value },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/contacts/${contactId.value}`;
  }
};

// ── Time in stage ─────────────────────────────────────────────────────────────

const timeInStage = computed(() => {
  const dateStr = props.item?.stage_entered_at || props.item?.created_at;
  if (!dateStr) return '0m';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
});

// ── Checklist progress ────────────────────────────────────────────────────────

const checklistPercent = computed(() => {
  if (!props.checklistTotal) return 0;
  return Math.round((props.checklistCompleted / props.checklistTotal) * 100);
});

// ── Item info — field values ──────────────────────────────────────────────────

// "Valor" mirrors the backend's `effective_value`: the manually-entered
// item_details.value takes precedence, falling back to the expected_revenue
// column. Without the fallback the field rendered "—" after marking a deal won
// (mark_as_won! writes expected_revenue / won_value, never item_details.value).
const itemValue = computed(() => {
  const manual = props.item?.item_details?.value;
  if (manual !== undefined && manual !== null && manual !== '') return manual;
  const revenue = props.item?.expected_revenue;
  return revenue !== undefined && revenue !== null && revenue !== ''
    ? revenue
    : '';
});
const deadlineAt = computed(() => props.item?.item_details?.deadline_at ?? '');
const forecastCloseDate = computed(
  () => props.item?.item_details?.forecast_close_date ?? ''
);
const leadScore = computed(
  () => props.item?.lead_score ?? props.item?.item_details?.lead_score ?? null
);

// ── Linked conversation ───────────────────────────────────────────────────────

const conversationData = computed(
  () => props.item?.item_details?.conversation || null
);

const hasLinkedConversation = computed(
  () => !!(conversationData.value || props.item?.item_details?.conversation_id)
);

const conversationDisplayId = computed(
  () =>
    conversationData.value?.display_id ||
    props.item?.conversation_display_id ||
    null
);

const conversationStatus = computed(
  () => conversationData.value?.status || null
);

const lastMessagePreview = computed(
  () => props.item?.item_details?.conversation_last_message_preview || null
);

const statusBadgeClass = computed(() => {
  const s = conversationStatus.value;
  if (s === 'open') return 'bg-emerald-3 text-emerald-11';
  if (s === 'resolved') return 'bg-n-alpha-1 text-n-slate-11';
  if (s === 'pending') return 'bg-amber-3 text-amber-11';
  return 'bg-n-alpha-1 text-n-slate-11';
});

const navigateToConversation = () => {
  if (!conversationDisplayId.value) return;
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'inbox_conversation',
      params: { accountId, conversation_id: conversationDisplayId.value },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/conversations/${conversationDisplayId.value}`;
  }
};

const handleUnlinkConversation = () => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('PIPELINE.PANELS.UNLINK_CONFIRM'))) {
    emit('unlinkConversation');
  }
};

// ── Custom attributes ─────────────────────────────────────────────────────────

const customAttrValues = computed(
  () => props.item?.item_details?.custom_attributes || {}
);

// Contact custom attributes (read-only — set on the contact record)
const contactCustomAttrs = computed(
  () =>
    props.item?.contact?.custom_attributes ||
    props.item?.item_details?.conversation?.contact?.custom_attributes ||
    {}
);

const contactAttrEntries = computed(() =>
  props.contactCustomAttributes
    .map(attr => ({
      key: attr.attribute_key,
      label: attr.attribute_display_name || attr.attribute_key,
      value: contactCustomAttrs.value[attr.attribute_key],
    }))
    .filter(
      entry =>
        entry.value !== undefined && entry.value !== null && entry.value !== ''
    )
);

const getAttrValue = key => customAttrValues.value[key] ?? '';

const handleCustomAttrSave = (key, newValue) => {
  emit('updateField', `custom_attributes.${key}`, newValue);
};

const attrInputType = attr => {
  const displayType =
    attr?.attribute_display_type || attr?.attribute_key_type || 'text';
  if (displayType === 'number') return 'number';
  if (displayType === 'date') return 'date';
  if (displayType === 'currency') return 'currency';
  if (displayType === 'checkbox' || displayType === 'boolean') return 'text';
  return 'text';
};

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  panelOpen.value = loadPanelState();
});
</script>

<template>
  <div class="flex flex-col gap-3 p-4">
    <!-- ── Block 1: Contact ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.contact"
        @click="togglePanel('contact')"
      >
        <span>{{ t('PIPELINE.PANELS.CONTACT') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.contact }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div v-if="panelOpen.contact" class="px-4 py-3 border-t border-n-weak">
        <div class="flex items-center gap-3">
          <Avatar
            :src="contactAvatarUrl"
            :name="contactName || 'Contact'"
            :size="48"
            rounded-full
            class="flex-shrink-0"
          />
          <div class="min-w-0 flex-1">
            <button
              v-if="contactId"
              type="button"
              class="text-sm font-medium text-n-brand hover:underline text-left truncate block w-full"
              @click="navigateToContact"
            >
              {{ contactName || '—' }}
            </button>
            <span
              v-else
              class="text-sm font-medium text-n-slate-12 truncate block"
            >
              {{ contactName || '—' }}
            </span>
            <div
              v-if="contactPhone"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ contactPhone }}
            </div>
            <div
              v-if="contactEmail"
              class="text-xs text-n-slate-11 mt-0.5 truncate"
            >
              {{ contactEmail }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Block 2: Statistics ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.statistics"
        @click="togglePanel('statistics')"
      >
        <span>{{ t('PIPELINE.PANELS.STATISTICS') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.statistics }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.statistics"
        class="px-4 py-3 border-t border-n-weak space-y-1"
      >
        <!-- Time in Stage -->
        <div class="flex items-center justify-between py-1.5 text-sm">
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.TIME_IN_STAGE')
          }}</span>
          <span class="font-medium text-n-slate-12">{{ timeInStage }}</span>
        </div>

        <!-- Notes -->
        <button
          type="button"
          class="w-full flex items-center justify-between py-1.5 text-sm rounded hover:bg-n-alpha-1 px-1 -mx-1 transition-colors"
          @click="emit('goToTab', 'notes')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.NOTES_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{ notesCount }}</span>
        </button>

        <!-- Checklist -->
        <button
          type="button"
          class="w-full flex items-center justify-between py-1.5 text-sm rounded hover:bg-n-alpha-1 px-1 -mx-1 transition-colors"
          @click="emit('goToTab', 'checklist')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.CHECKLIST_COUNT')
          }}</span>
          <div class="flex items-center gap-2">
            <span class="font-medium text-n-slate-12 text-xs">
              {{ `${checklistCompleted}/${checklistTotal}` }}
            </span>
            <div class="w-16 h-1.5 bg-n-weak rounded-full overflow-hidden">
              <div
                class="h-full bg-n-brand rounded-full transition-all duration-300"
                :style="{ width: `${checklistPercent}%` }"
              />
            </div>
          </div>
        </button>

        <!-- Attachments -->
        <button
          type="button"
          class="w-full flex items-center justify-between py-1.5 text-sm rounded hover:bg-n-alpha-1 px-1 -mx-1 transition-colors"
          @click="emit('goToTab', 'attachments')"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.ATTACHMENTS_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{
            attachmentsCount
          }}</span>
        </button>

        <!-- Stage Changes (non-clickable — history tab removed) -->
        <div
          class="flex items-center justify-between py-1.5 text-sm px-1 -mx-1"
        >
          <span class="text-n-slate-11">{{
            t('PIPELINE.PANELS.STAGE_CHANGES_COUNT')
          }}</span>
          <span class="font-medium text-n-slate-12">{{
            stageChangesCount
          }}</span>
        </div>
      </div>
    </div>

    <!-- ── Block 3: Item Information ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.itemInfo"
        @click="togglePanel('itemInfo')"
      >
        <span>{{ t('PIPELINE.PANELS.ITEM_INFO') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.itemInfo }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.itemInfo"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <!-- Value -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.VALUE') }}
          </p>
          <InlineEditField
            :model-value="itemValue"
            type="currency"
            empty-label="—"
            @save="val => emit('updateField', 'value', val)"
          />
        </div>

        <!-- Deadline -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.DEADLINE') }}
          </p>
          <InlineEditField
            :model-value="deadlineAt"
            type="datetime"
            empty-label="—"
            @save="val => emit('updateField', 'deadline_at', val)"
          />
        </div>

        <!-- Forecast Close Date -->
        <div>
          <p class="text-xs text-n-slate-10 mb-1">
            {{ t('PIPELINE.PANELS.FORECAST_CLOSE') }}
          </p>
          <InlineEditField
            :model-value="forecastCloseDate"
            type="date"
            empty-label="—"
            @save="val => emit('updateField', 'forecast_close_date', val)"
          />
        </div>

        <!-- Lead Score -->
        <div>
          <div class="flex items-center justify-between">
            <p class="text-xs text-n-slate-10">
              {{ t('PIPELINE.PANELS.LEAD_SCORE') }}
            </p>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="emit('overrideLeadScore')"
            >
              {{ t('PIPELINE.PANELS.OVERRIDE_LEAD_SCORE') }}
            </button>
          </div>
          <span class="text-sm font-medium text-n-slate-12">
            {{
              leadScore !== null
                ? leadScore
                : t('PIPELINE.INLINE_EDIT.EMPTY_VALUE')
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Block 4: Linked Conversation ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.linkedConversation"
        @click="togglePanel('linkedConversation')"
      >
        <span>{{ t('PIPELINE.PANELS.LINKED_CONVERSATION') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.linkedConversation }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.linkedConversation"
        class="px-4 py-3 border-t border-n-weak"
      >
        <!-- Linked -->
        <div v-if="hasLinkedConversation" class="space-y-2">
          <div class="flex items-center justify-between gap-2">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ `#${conversationDisplayId}` }}
                <span v-if="contactName" class="text-n-slate-11 font-normal">
                  {{ `— ${contactName}` }}
                </span>
              </p>
              <p
                v-if="lastMessagePreview"
                class="text-xs text-n-slate-10 truncate mt-0.5"
              >
                {{ lastMessagePreview }}
              </p>
              <span
                v-if="conversationStatus"
                class="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-xs font-medium"
                :class="statusBadgeClass"
              >
                {{ conversationStatus }}
              </span>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                class="px-2 py-1 text-xs font-medium text-n-slate-11 border border-n-weak rounded hover:bg-n-alpha-1 transition-colors"
                @click="navigateToConversation"
              >
                {{ t('PIPELINE.PANELS.VIEW_CONVERSATION') }}
              </button>
              <button
                type="button"
                class="px-2 py-1 text-xs font-medium text-ruby-11 border border-ruby-6 rounded hover:bg-ruby-3 transition-colors"
                @click="handleUnlinkConversation"
              >
                {{ t('PIPELINE.PANELS.UNLINK_CONVERSATION') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Not linked -->
        <div v-else>
          <button
            type="button"
            class="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-n-brand border border-dashed border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors"
            @click="emit('linkConversation')"
          >
            {{ t('PIPELINE.PANELS.LINK_CONVERSATION_BTN') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Block 5: Additional Data (custom attributes) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.additionalData"
        @click="togglePanel('additionalData')"
      >
        <span>{{ t('PIPELINE.PANELS.ADDITIONAL_DATA') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.additionalData }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.additionalData"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <template v-if="customAttributes.length > 0">
          <div
            v-for="attr in customAttributes"
            :key="attr.attribute_key || attr.id"
          >
            <p class="text-xs text-n-slate-10 mb-1">
              {{ attr.attribute_display_name || attr.attribute_key }}
            </p>
            <InlineEditField
              :model-value="getAttrValue(attr.attribute_key)"
              :type="attrInputType(attr)"
              empty-label="—"
              @save="val => handleCustomAttrSave(attr.attribute_key, val)"
            />
          </div>
        </template>

        <p v-else class="text-sm text-n-slate-10 italic">
          {{ t('PIPELINE.PANELS.EMPTY_CUSTOM_ATTRIBUTES') }}
        </p>
      </div>
    </div>

    <!-- ── Block 6: Custom Fields (contact custom attributes, read-only) ── -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden">
      <button
        type="button"
        class="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-n-slate-12 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :aria-expanded="panelOpen.customFields"
        @click="togglePanel('customFields')"
      >
        <span>{{ t('PIPELINE.PANELS.CUSTOM_FIELDS') }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 transition-transform duration-200"
          :class="{ 'rotate-180': panelOpen.customFields }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-if="panelOpen.customFields"
        class="px-4 py-3 border-t border-n-weak space-y-3"
      >
        <template v-if="contactAttrEntries.length > 0">
          <div
            v-for="entry in contactAttrEntries"
            :key="entry.key"
            class="flex flex-col gap-1"
          >
            <p class="text-xs text-n-slate-10">{{ entry.label }}</p>
            <p class="text-sm text-n-slate-12 break-words">{{ entry.value }}</p>
          </div>
        </template>

        <p v-else class="text-sm text-n-slate-10 italic">
          {{ t('PIPELINE.PANELS.EMPTY_CUSTOM_FIELDS') }}
        </p>
      </div>
    </div>
  </div>
</template>
